import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import StatusBadge from '../../components/invoice/StatusBadge';
import { useToast } from '../../context/ToastContext';
import invoiceService from '../../services/invoiceService';
import {
  formatCurrency, formatDateTime, formatMonthLabel
} from '../../utils/invoiceFormatters';

const STATUS_ORDER = ['DRAFT', 'GENERATED', 'SENT', 'PAID'];

const InvoiceDetail = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { error: toastError, success: toastSuccess, info: toastInfo } = useToast();
  const showToast = (msg, type) => {
    if (type === 'success') toastSuccess(msg);
    else if (type === 'error') toastError(msg);
    else toastInfo(msg);
  };
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await invoiceService.getInvoice(invoiceId);
        setInvoice(res.data);
      } catch {
        setError('Failed to load invoice details.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [invoiceId]);

  const handleStatusUpdate = async (newStatus) => {
    setConfirmStatus(null);
    setActionLoading(true);
    try {
      const res = await invoiceService.updateStatus(invoiceId, newStatus);
      setInvoice(res.data);
      showToast(`Invoice marked as ${newStatus.toLowerCase()}.`, 'success');
    } catch {
      showToast('Status update failed. Please try again.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerate = async () => {
    setConfirmStatus(null);
    setActionLoading(true);
    try {
      const res = await invoiceService.generateInvoice({
        enterpriseId: invoice.enterpriseId,
        invoiceMonth: `${invoice.invoiceYear}-${String(invoice.invoiceMonth).padStart(2, '0')}`,
      });
      setInvoice(res.data);
      showToast('Invoice regenerated successfully.', 'success');
    } catch (e) {
      showToast(e.response?.data?.error || 'Failed to regenerate invoice.', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="w-full px-4 py-8">
          <div className="h-8 bg-gray-200 rounded animate-pulse w-64 mb-6" />
          <div className="bg-white rounded-xl border border-gray-100 p-8 space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-8 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="min-h-screen bg-gray-50">
        
        <div className="w-full px-4 py-8 text-center">
          <p className="text-gray-500 mb-4">{error || 'Invoice not found.'}</p>
          <button onClick={() => navigate(-1)}
            className="text-sm text-primary border border-primary px-4 py-2 rounded-lg hover:bg-primary/5">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const currentIdx = STATUS_ORDER.indexOf(invoice.status);

  // Action button logic
  const renderActionButton = () => {
    if (actionLoading) {
      return (
        <button disabled className="px-4 py-2 text-sm bg-gray-100 text-gray-400 rounded-lg flex items-center gap-2">
          <span className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
          Processing...
        </button>
      );
    }
    switch (invoice.status) {
      case 'DRAFT':
        return (
          <button onClick={() => setConfirmStatus('GENERATED')}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-light transition-colors">
            Mark as Generated
          </button>
        );
      case 'GENERATED':
        return (
          <button onClick={() => setConfirmStatus('SENT')}
            className="px-4 py-2 text-sm border border-primary text-primary rounded-lg hover:bg-primary/5 transition-colors">
            Mark as Sent
          </button>
        );
      case 'SENT':
        return (
          <button onClick={() => setConfirmStatus('PAID')}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-light transition-colors">
            Mark as Paid
          </button>
        );
      case 'PAID':
        return null;
      default:
        return (
          <button onClick={() => setConfirmStatus('REGEN')}
            className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors">
            Retry Generation
          </button>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      
      <div className="w-full px-4 py-8">

        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-gray-600 text-sm">←</button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-800">{invoice.invoiceNumber}</h1>
                <StatusBadge status={invoice.status} />
              </div>
              <p className="text-xs text-gray-400 mt-0.5">
                {invoice.enterpriseName || `Enterprise #${invoice.enterpriseId}`} &middot;{' '}
                {formatMonthLabel(invoice.invoiceMonth, invoice.invoiceYear)}
              </p>
            </div>
          </div>
          <div>{renderActionButton()}</div>
        </div>

        {/* Status progression */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-5">
          <div className="flex items-center justify-between">
            {STATUS_ORDER.map((s, i) => (
              <React.Fragment key={s}>
                <div className="flex flex-col items-center gap-1">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    i <= currentIdx ? 'bg-primary text-white' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {i <= currentIdx ? '✓' : i + 1}
                  </div>
                  <span className={`text-xs ${i <= currentIdx ? 'text-primary font-medium' : 'text-gray-400'}`}>
                    {s}
                  </span>
                </div>
                {i < STATUS_ORDER.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 ${i < currentIdx ? 'bg-primary' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Info cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs text-gray-400 font-medium uppercase mb-3">Enterprise Details</h3>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-xs text-gray-400">Name</p>
                <p className="font-medium text-gray-800">{invoice.enterpriseName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Billing Period</p>
                <p className="font-medium text-gray-800">
                  {formatMonthLabel(invoice.invoiceMonth, invoice.invoiceYear)}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
            <h3 className="text-xs text-gray-400 font-medium uppercase mb-3">Billing Details</h3>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-xs text-gray-400">Billing Number</p>
                <p className="font-mono font-medium text-gray-800">{invoice.invoiceNumber}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Generated By</p>
                <p className="font-medium text-gray-800">{invoice.generatedByName || 'Scheduler'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Generated At</p>
                <p className="text-gray-700">{formatDateTime(invoice.generatedAt)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400">Total Amount</p>
                <p className="text-lg font-bold text-gray-800">{formatCurrency(invoice.totalAmount)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-700">Line Items</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase bg-gray-50">
                  <th className="text-left px-5 py-3 font-medium">Component</th>
                  <th className="text-left px-5 py-3 font-medium">Description</th>
                  <th className="text-right px-5 py-3 font-medium">Qty</th>
                  <th className="text-right px-5 py-3 font-medium">Rate</th>
                  <th className="text-right px-5 py-3 font-medium">Actual</th>
                  <th className="text-right px-5 py-3 font-medium">MMG Floor</th>
                  <th className="text-right px-5 py-3 font-medium">Billed</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {(invoice.lineItems || []).map((item) => {
                  const hasMmg = item.mmgFloor && Number(item.mmgFloor) > 0;
                  return (
                    <tr key={item.id} className={hasMmg ? 'bg-amber-50' : ''}>
                      <td className="px-5 py-3 font-medium text-gray-700">
                        {item.component}
                        {hasMmg && <span className="ml-1 text-amber-600 text-xs">⚡MMG</span>}
                      </td>
                      <td className="px-5 py-3 text-gray-500">{item.description}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{item.quantity}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{formatCurrency(item.unitRate)}</td>
                      <td className="px-5 py-3 text-right text-gray-600">{formatCurrency(item.actualCalculated)}</td>
                      <td className="px-5 py-3 text-right text-gray-600">
                        {hasMmg ? formatCurrency(item.mmgFloor) : '—'}
                      </td>
                      <td className="px-5 py-3 text-right font-semibold" style={{ color: '#27500A' }}>
                        {formatCurrency(item.amount)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 font-bold">
                  <td colSpan={6} className="px-5 py-3 text-gray-700">Grand Total</td>
                  <td className="px-5 py-3 text-right text-lg" style={{ color: '#27500A' }}>
                    {formatCurrency(invoice.totalAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* Confirm dialog */}
      {confirmStatus && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 shadow-xl">
            <h3 className="font-semibold text-gray-800 mb-2">
              {confirmStatus === 'REGEN' ? 'Retry Generation?' : `Mark as ${confirmStatus}?`}
            </h3>
            <p className="text-sm text-gray-500 mb-5">
              {confirmStatus === 'REGEN'
                ? 'This will regenerate the invoice. The current data will be replaced.'
                : `This action cannot be undone. Status will change to ${confirmStatus}.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmStatus(null)}
                className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={() => confirmStatus === 'REGEN' ? handleRegenerate() : handleStatusUpdate(confirmStatus)}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary-light">
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetail;
